// The recipe catalog's contract. catalog.json is parsed with this at load, so a malformed entry
// fails tests and CI instead of reaching users.
//
// Review: only `approved` entries reach the app, and approval names who reviewed it and when.
// Provenance: a dish presented as traditional must say where it is from, cite at least one
// source and state how confident the attribution is. Nothing is labelled authentic by default.

import { z } from 'zod';
import { INGREDIENTS, type IngredientId } from '../taxonomy';

export const UNITS = ['g', 'ml', 'cup', 'tbsp', 'tsp', 'each', 'clove', 'slice', 'can', 'pinch', 'handful'] as const;
export const MEAL_TYPES = ['main', 'breakfast', 'side', 'snack'] as const;
export const RECIPE_TAGS = ['one_pot', 'budget', 'high_protein', 'sheet_pan', 'no_cook'] as const;
export const CUISINES = [
  'american', 'chinese', 'mexican', 'italian', 'indian', 'mediterranean',
  'middle_eastern', 'asian', 'british', 'french', 'everyday',
  'japanese', 'korean', 'thai', 'vietnamese', 'european', 'latin_american',
] as const;

/** The catalog's planning groups (the 500-meal allocation), separate from cuisine tags. */
export const CATEGORIES = [
  'american', 'european', 'east_asian', 'south_southeast_asian', 'middle_east_north_africa',
  'latin_american', 'african_caribbean', 'international',
] as const;
export const EQUIPMENT = ['stovetop', 'oven', 'blender', 'toaster'] as const;

const ingredientId = z.string().refine((x): x is IngredientId => x in INGREDIENTS, { message: 'not a taxonomy ingredient id' });
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/);
const text = z.string().trim().min(1);

const ingredient = z.object({
  id: ingredientId,
  qty: z.number().positive().optional(),
  unit: z.enum(UNITS).optional(),
  role: z.enum(['core', 'pantry', 'optional']),
  cooked: z.literal(true).optional(),
}).strict().refine((i) => (i.qty === undefined) === (i.unit === undefined), { message: 'qty and unit go together' });

const step = z.object({
  text,
  timerMin: z.number().positive().max(240).optional(),
  safety: text.optional(),
}).strict();

const meta = z.object({
  cuisine: z.enum(CUISINES),
  heat: z.union([z.literal(0), z.literal(1), z.literal(2)]),
  equipment: z.array(z.enum(EQUIPMENT)), // empty for no-cook dishes
  visual: z.object({
    dish: text,
    visible: z.array(text).min(1),
    method: text,
    presentation: text,
    palette: z.tuple([hex, hex, hex]),
  }).strict(),
}).strict();

const reference = z.object({ title: text, url: z.string().url(), accessed: isoDate }).strict();

const provenance = z.object({
  /** house: written from standard technique. adapted: a known dish simplified. traditional: as documented. */
  source: z.enum(['house', 'adapted', 'traditional']),
  /** Country, region or community, when the dish is attributed to one. */
  origin: text.optional(),
  localNames: z.array(text).optional(),
  references: z.array(reference),
  attributionConfidence: z.enum(['high', 'medium', 'low']).optional(),
  /** What an adapted version changes from the documented dish, said plainly. */
  adaptationNotes: text.optional(),
}).strict().superRefine((p, ctx) => {
  if (p.source === 'house') return;
  if (!p.origin) ctx.addIssue({ code: 'custom', message: `${p.source} dishes need an origin` });
  if (!p.references.length) ctx.addIssue({ code: 'custom', message: `${p.source} dishes need at least one reference` });
  if (!p.attributionConfidence) ctx.addIssue({ code: 'custom', message: `${p.source} dishes need an attribution confidence` });
  if (p.source === 'adapted' && !p.adaptationNotes) ctx.addIssue({ code: 'custom', message: 'adapted dishes must say what changed' });
});

const review = z.discriminatedUnion('status', [
  z.object({ status: z.literal('draft'), notes: text.optional() }).strict(),
  z.object({ status: z.literal('approved'), reviewedBy: text, reviewedAt: isoDate, notes: text.optional() }).strict(),
]);

export const entrySchema = z.object({
  id: z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'kebab-case id'),
  title: text,
  minutes: z.number().int().positive().max(600),
  difficulty: z.enum(['easy', 'medium']),
  servings: z.number().int().min(1).max(12),
  meal: z.enum(MEAL_TYPES),
  category: z.enum(CATEGORIES),
  tags: z.array(z.enum(RECIPE_TAGS)),
  ingredients: z.array(ingredient).min(1).refine((xs) => xs.some((i) => i.role === 'core'), { message: 'needs a core ingredient' }),
  steps: z.array(step).min(1),
  meta,
  provenance,
  review,
  /** Cooked and signed off in a real kitchen. */
  tested: z.boolean(),
}).strict();

export const catalogSchema = z.object({
  version: z.literal(1),
  recipes: z.array(entrySchema),
}).strict().superRefine((c, ctx) => {
  const seen = new Set<string>();
  for (const r of c.recipes) {
    if (seen.has(r.id)) ctx.addIssue({ code: 'custom', message: `duplicate recipe id ${r.id}` });
    seen.add(r.id);
  }
});

export type CatalogEntry = z.infer<typeof entrySchema>;
